import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  List, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  Divider, 
  Paper,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  useTheme
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Instagram as InstagramIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { clientService } from '../services/supabaseClient';
import { Client } from '../types';
import ConnectInstagram from './ConnectInstagram';
import { getAuthorizationUrl, InstagramAuthData } from '../services/instagramAuthService';
import { subscriptionLimitsService } from '../services/subscriptionLimitsService';
import SubscriptionLimitsAlert from './SubscriptionLimitsAlert';
import { GLASS } from '../theme/glassTokens';
import { deriveInternalInstagramSlug, getClientInstagramDisplay } from '../utils/clientDisplay';

interface ClientManagerProps {
  clients: Client[];
  // Props opcionais para compatibilidade com diferentes usos
  onAddClient?: (client: Client) => void;
  onSelectClient?: (clientId: string) => void;
  selectedClientId?: string;
  // Novas props para o StoryCalendar
  onClientAdded?: (newClient: Omit<Client, 'id'>) => Promise<void>;
  onClientUpdated?: (updatedClient: Client) => void;
  onClientDeleted?: (clientId: string) => void;
  // Prop para mostrar apenas o formulário de adicionar (sem lista)
  addOnly?: boolean;
}

const ClientManager: React.FC<ClientManagerProps> = ({ 
  clients, 
  onAddClient,
  onSelectClient,
  selectedClientId,
  onClientAdded,
  onClientUpdated,
  onClientDeleted,
  addOnly = false
}) => {
  const _theme = useTheme();
  const [name, setName] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [appId, setAppId] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<number>(0);

  const startInstagramOAuthForClient = (clientId: string) => {
    localStorage.setItem('current_client_id', clientId);
    localStorage.removeItem('instagram_auth_temp_data');
    localStorage.removeItem('instagram_auth_error');
    localStorage.removeItem('instagram_auth_success');

    const authUrl = getAuthorizationUrl(clientId);
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'instagram-auth',
      `width=${width},height=${height},top=${top},left=${left}`,
    );

    if (!popup) {
      throw new Error('Cliente criado, mas não foi possível abrir o popup do Instagram. Habilite popups e tente novamente.');
    }
  };

  const handleAddClient = async (connectInstagram = true) => {
    if (!name) {
      setError('Nome do cliente é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const limitCheck = await subscriptionLimitsService.canCreateClient();
      
      if (!limitCheck.allowed) {
        setError(limitCheck.message || 'Não é possível adicionar mais clientes. Faça upgrade do seu plano.');
        setLoading(false);
        return;
      }

      const clientData = {
        name,
        instagram: deriveInternalInstagramSlug(name),
        logoUrl,
        appId,
        accessToken,
        userId
      };

      let createdClient: Client | null = null;

      // Se temos onClientAdded (usado no StoryCalendar), usar ela
      if (onClientAdded) {
        await onClientAdded(clientData);
      } else {
        // Caso contrário, criar o cliente e notificar via onAddClient
        const newClient = await clientService.addClient(clientData);
        createdClient = newClient;
        if (onAddClient) {
          onAddClient(newClient);
        }
      }

      // Limpar formulário
      setName('');
      setLogoUrl('');
      setAppId('');
      setAccessToken('');
      setUserId('');

      if (createdClient?.id && connectInstagram) {
        startInstagramOAuthForClient(createdClient.id);
      }

      // Se addOnly, o componente pai deve fechar o modal
      // Não precisamos fazer nada aqui, apenas limpar o formulário

    } catch (error: any) {
      console.error('Erro ao adicionar cliente:', error);
      // Se o erro for sobre limite, mostrar mensagem específica
      if (error.message && error.message.includes('limite')) {
        setError(error.message);
      } else {
        setError('Erro ao adicionar cliente. ' + (error.message || ''));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    setLoading(true);
    setError(null);

    try {
      await clientService.deleteClient(clientToDelete.id);
      
      // Notificar o componente pai sobre a exclusão
      if (onClientDeleted) {
        onClientDeleted(clientToDelete.id);
      }
      
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      setError('Erro ao excluir cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = (client: Client) => {
    if (onSelectClient) {
      onSelectClient(client.id);
    }
  };

  const handleConfirmDelete = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleConnectionUpdate = (clientId: string, instagramData: InstagramAuthData | null) => {
    // Dados já persistidos em `saveInstagramAuth` no filho — não chamar `updateClient` aqui
    // (evita regravar `name` placeholder antigo da lista em memória).
    if (instagramData && onClientUpdated) {
      const clientToUpdate = clients.find(c => c.id === clientId);
      if (clientToUpdate) {
        onClientUpdated({
          ...clientToUpdate,
          name: instagramData.clientName ?? clientToUpdate.name,
          instagram: instagramData.username || clientToUpdate.instagram,
          instagramAccountId: instagramData.instagramAccountId,
          accessToken: instagramData.accessToken,
          username: instagramData.username,
          profilePicture: instagramData.profilePicture,
          tokenExpiry: instagramData.tokenExpiry,
          pageId: instagramData.pageId,
          pageName: instagramData.pageName,
          instagramLongLivedIssuedAt: instagramData.issuedAt,
        });
      }
    }
  };

  // Função para obter a imagem do avatar
  const getAvatarImage = (client: Client) => {
    // Priorizar a foto do perfil do Instagram
    if (client.profilePicture) {
      return client.profilePicture;
    }
    // Fallback para o logo do cliente
    return client.logoUrl;
  };

  // Filtrar clientes com base na pesquisa
  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    client.instagram.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Se addOnly for true, mostrar apenas o formulário de adicionar
  if (addOnly) {
    return (
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Alert de limites de subscription */}
        <SubscriptionLimitsAlert type="client" />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            label="Nome do Cliente"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          
          <TextField
            fullWidth
            label="Instagram"
            variant="outlined"
            value="Opcional — conecte depois se precisar publicar automaticamente"
            disabled
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleAddClient(false)}
              disabled={loading || !name}
            >
              {loading ? <CircularProgress size={24} /> : 'Criar sem Instagram'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleAddClient(true)}
              disabled={loading || !name}
            >
              {loading ? <CircularProgress size={24} /> : 'Criar e conectar Instagram'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Tabs 
        value={currentTab} 
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: '#f9f9f9'
        }}
      >
        <Tab label="Lista de Clientes" />
        <Tab label="Adicionar Cliente" />
      </Tabs>

      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {filteredClients.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              py: 4
            }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nenhum cliente encontrado
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={() => setCurrentTab(1)}
              >
                Adicionar Cliente
              </Button>
            </Box>
          ) : (
            <List sx={{ 
              maxHeight: 'calc(100vh - 300px)', 
              overflow: 'auto',
              '& .MuiListItem-root': {
                borderRadius: 1,
                mb: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                bgcolor: 'background.paper'
              }
            }}>
              {filteredClients.map((client) => (
                <Paper 
                  key={client.id} 
                  elevation={0}
                  sx={{ 
                    mb: 2, 
                    borderRadius: GLASS.radius.inner,
                    border: client.id === selectedClientId ? `2px solid ${GLASS.accent.orange}` : `1px solid ${GLASS.border.outer}`,
                    overflow: 'hidden',
                    bgcolor: GLASS.surface.bg,
                    backdropFilter: `blur(${GLASS.surface.blur})`,
                    WebkitBackdropFilter: `blur(${GLASS.surface.blur})`,
                    boxShadow: `${GLASS.shadow.card}, ${GLASS.shadow.cardInset}`,
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 0 },
                    p: 2
                  }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: onSelectClient ? 'pointer' : 'default',
                        flexGrow: 1
                      }}
                      onClick={() => onSelectClient && handleSelectClient(client)}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          src={getAvatarImage(client)} 
                          alt={client.name}
                          sx={{ 
                            width: 56, 
                            height: 56,
                            border: client.profilePicture ? `2px solid ${GLASS.accent.orange}` : 'none'
                          }}
                        >
                          {client.name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {client.name}
                            </Typography>
                            {client.profilePicture && (
                              <InstagramIcon sx={{ fontSize: 16, color: GLASS.accent.orange }} />
                            )}
                          </Box>
                        } 
                        secondary={
                          <Typography component="span" variant="body2">
                            {getClientInstagramDisplay(client) ?? 'Só aprovação — sem Instagram'}
                          </Typography>
                        }
                      />
                    </Box>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleConfirmDelete(client)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Divider />
                  
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <InstagramIcon fontSize="small" sx={{ mr: 1, color: GLASS.accent.orange }} />
                      Conexão com Instagram
                    </Typography>
                    
                    <ConnectInstagram 
                      client={client}
                      onConnectionUpdate={handleConnectionUpdate}
                    />
                  </Box>
                </Paper>
              ))}
            </List>
          )}
        </Box>
      )}

      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Adicionar Novo Cliente
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
              <TextField
                fullWidth
                label="Nome do Cliente"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Box>
            
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
              <TextField
                fullWidth
                label="Instagram"
                variant="outlined"
                value="Opcional — conecte depois se precisar publicar automaticamente"
                disabled
              />
            </Box>
            
            <Box sx={{ flex: '1 1 100%', display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddClient(false)}
                disabled={loading || !name}
              >
                {loading ? <CircularProgress size={24} /> : 'Criar sem Instagram'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddClient(true)}
                disabled={loading || !name}
              >
                {loading ? <CircularProgress size={24} /> : 'Criar e conectar Instagram'}
              </Button>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            Use &quot;Criar sem Instagram&quot; para clientes que só precisam aprovar conteúdo. O nome informado aparece nos links de aprovação.
          </Typography>
        </Box>
      )}

      {/* Diálogo de confirmação para exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: GLASS.radius.card,
            bgcolor: GLASS.surface.bgStrong,
            backdropFilter: `blur(${GLASS.surface.blurStrong})`,
            WebkitBackdropFilter: `blur(${GLASS.surface.blurStrong})`,
            border: `1px solid ${GLASS.border.outer}`,
            boxShadow: `${GLASS.shadow.card}, ${GLASS.shadow.cardInset}`,
          }
        }}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cliente "{clientToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteClient} 
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManager;